import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import useContractTypeService from '../../../app/services/hooks/useContractTypeService';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import ButtonGeneral from '../../../components/buttons/ButtonGeneral';
import ButtonTableAction from '../../../components/buttons/ButtonTableAction';
import DefaultDataTable from '../../../components/default/DefaultDataTable';
import DefaultCard from '../../../components/default/DefaultCard';
import PageHeader from '../../../components/PageHeader/PageHeader';
import InputField from '../../../components/InputField';
import { AppContext } from '../../../contexts/AppContext';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { formatMoney } from '../../../utils/utils';

interface ContractType {
    id: number;
    name: string;
    description: string;
    icon: string;
    price: string;
    has_articles: boolean;
    is_signature_only: boolean;
    is_active: boolean;
    created_at: string;
}

const ContractTypeList: React.FC = () => {
    const history = useHistory();
    const { showLoading, hideLoading } = useContext(AppContext);
    const { getAllContractTypes, activeContractType, deleteContractType, fetchingAll, fetchingActive, fetchingDelete } = useContractTypeService();
    const { requestConfirmation } = useReactConfirmAlert();
    const [contractTypes, setContractTypes] = useState<ContractType[]>([]);
    const [totalRows, setTotalRows] = useState(0);
    const [filter, setFilter] = useState({
        page: 1,
        per_page: 10,
        search: ''
    });

    useEffect(() => {
        reloadTable();
    }, [filter.page, filter.per_page, filter.search]);

    useEffect(() => {
        if (fetchingAll || fetchingActive || fetchingDelete) {
            showLoading?.();
        } else {
            hideLoading?.();
        }
    }, [fetchingAll, fetchingActive, fetchingDelete]);

    const reloadTable = () => {
        getAllContractTypes({
            page: filter.page,
            page_size: filter.per_page,
            search: filter.search
        }, {
            onSuccess: (res: ServiceResponse) => {
                setContractTypes(res.data.contract_types || []);
                setTotalRows(res.data.total_rows || res.data.total || 0);
            },
            onError: (res: ServiceResponse) => {
                toast.error(res.message || 'Error al cargar tipos de contrato');
            }
        });
    };

    const handleActive = (id: number) => {
        activeContractType(id, {
            onSuccess: (res) => {
                toast.success(res.message);
                reloadTable();
            },
            onError: (res) => {
                toast.error(res.message);
            }
        });
    };

    const handleDelete = (id: number, name: string) => {
        requestConfirmation({
            title: '¿Eliminar tipo de contrato?',
            message: `¿Está seguro que desea eliminar "${name}"?`,
            buttons: {
                confirmButton: {
                    label: 'Sí, eliminar',
                    onClick: () => {
                        deleteContractType(id, {
                            onSuccess: (res) => {
                                toast.success(res.message);
                                reloadTable();
                            },
                            onError: (res) => {
                                toast.error(res.message);
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'Cancelar',
                    onClick: () => {
                        // No hacer nada
                    }
                }
            }
        });
    };

    const columns = [
        {
            name: 'Nombre',
            selector: (row: ContractType) => row.name,
            sortable: true,
            cell: (row: ContractType) => (
                <div>
                    <strong>{row.name}</strong>
                    {row.is_signature_only && (
                        <span className="badge bg-primary ms-2">Solo firmas</span>
                    )}
                </div>
            )
        },
        {
            name: 'Descripción',
            selector: (row: ContractType) => row.description,
            sortable: true
        },
        {
            name: 'Precio',
            selector: (row: ContractType) => row.price,
            sortable: true,
            cell: (row: ContractType) => (
                <span className="fw-bold">{formatMoney(parseFloat(row.price || '0'), 'CLP')}</span>
            )
        },
        {
            name: 'Artículos',
            selector: (row: ContractType) => row.has_articles,
            sortable: true,
            cell: (row: ContractType) => (
                row.has_articles ? <span className="badge bg-info">Sí</span> : <span className="badge bg-secondary">No</span>
            )
        },
        {
            name: 'Estado',
            selector: (row: ContractType) => row.is_active,
            sortable: true,
            cell: (row: ContractType) => (
                row.is_active ? <span className="badge bg-success">Activo</span> : <span className="badge bg-danger">Inactivo</span>
            )
        },
        {
            name: 'Acciones',
            selector: (row: ContractType) => row.id,
            sortable: false,
            cell: (row: ContractType) => (
                <div className="d-flex align-items-center">
                    <ButtonTableAction
                        callbackFunction={() => history.push(`/intranet/contract-types/${row.id}/edit`)}
                        title="Editar"
                        classIcon="fa-edit"
                        colorIcon="text-warning"
                        sizeIcon="16"
                    />
                    <ButtonTableAction
                        callbackFunction={() => history.push(`/intranet/contract-types/${row.id}/config`)}
                        title="Configurar"
                        classIcon="fa-cog"
                        colorIcon="text-info"
                        sizeIcon="16"
                    />
                    <ButtonTableAction
                        callbackFunction={() => handleActive(row.id)}
                        title={row.is_active ? 'Desactivar' : 'Activar'}
                        classIcon={row.is_active ? 'fa-toggle-on' : 'fa-toggle-off'}
                        colorIcon={row.is_active ? 'text-success' : 'text-secondary'}
                        sizeIcon="16"
                    />
                    <ButtonTableAction
                        callbackFunction={() => handleDelete(row.id, row.name)}
                        title="Eliminar"
                        classIcon="fa-trash"
                        colorIcon="text-danger"
                        sizeIcon="16"
                    />
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: '180px'
        }
    ];

    return (
        <div className="container-fluid">
            <PageHeader
                title=""
                breadcrumbs={[
                    { label: 'Inicio', path: '/intranet' },
                    { label: 'Configuración', path: '#' },
                    { label: 'Tipos de Contrato', isCurrent: true }
                ]}
            />

            <DefaultCard>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h1 className="page-title">Tipos de Contrato</h1>
                        <p className="page-description">Gestión de tipos de contrato disponibles</p>
                    </div>
                    <ButtonGeneral
                        type="button"
                        variant="primary"
                        title="Nuevo Tipo"
                        onClick={() => history.push('/intranet/contract-types/create')}
                        icon="fa-plus"
                        size="small"
                    />
                </div>

                {/* Filtros */}
                <div className="row mb-3">
                    <div className="col-md-4">
                        <InputField
                            label="Buscar"
                            value={filter.search}
                            onChange={(value) => setFilter({ ...filter, search: value, page: 1 })}
                            placeholder="Buscar por nombre o descripción..."
                        />
                    </div>
                </div>

                {/* Data Table */}
                <DefaultDataTable
                    columns={columns}
                    data={contractTypes}
                    paginationTotalRows={totalRows}
                    filter={filter}
                    setFilter={setFilter}
                    pagination={true}
                    progressPending={fetchingAll}
                />
            </DefaultCard>
        </div>
    );
};

export default ContractTypeList;

